import { nextTick } from 'vue';
import useAppI18n from './use-app-i18n';
import { IRouteMeta, ResolveParams } from '../models/routing';
import { AppCoreEnums } from '../shared/enums';
import { ROUTE_CHAT } from '@/router/chat/routes';
import { ROUTE_MAIN } from '../../router/main/routes';
import { useCoreStore } from '../../stores/core/store';
import useMainMenuItems from '../../composables/use-main-menu-items';
import { AppMainMenuItem, BreadcrumbItem } from '../../stores/core/models';
import { RouteLocationNormalized, RouteRecordNormalized } from 'vue-router';

export default () => {
  const { t } = useAppI18n();
  const coreStore = useCoreStore();
  const { leftMenuItems } = useMainMenuItems();

  const getMenuItem = (routeName: string): AppMainMenuItem | undefined => {
    let menuItem: AppMainMenuItem | undefined = undefined;
    menuItem = leftMenuItems.value.find((x) => x.route === routeName);

    if (!menuItem) {
      leftMenuItems.value
        .filter((x) => x.items?.length)
        .forEach((item) => {
          menuItem = item.items?.find((y: AppMainMenuItem) => y.route === routeName);
          return !!menuItem;
        });
    }
    return menuItem;
  };

  const buildBreadcrumbItems = async (route: RouteLocationNormalized) => {
    const addItemForTabsContainer = async (
      routeMatched: RouteRecordNormalized,
      parentRouteMatched: RouteRecordNormalized | undefined
    ) => {
      const containerData = coreStore.getTableBasedContainerData(AppCoreEnums.ContainerType.CollapsibleTableContainer).value;

      const id = route.params['id'];
      let selectedRow = containerData?.options?.rows.find((x) => x[containerData.options.idPropertyName] == id);
      
      // If not found in current page data, check if form container has the data
      if (!selectedRow && id) {
        const { useIndexesStore } = await import('@/stores');
        const indexesStore = useIndexesStore();
        const formIndexData = indexesStore.get('IndexProperties').value;
        if (formIndexData && typeof formIndexData === 'object' && 'id' in formIndexData && formIndexData.id == Number(id)) {
          selectedRow = formIndexData;
        }
      }
      
      if (selectedRow) {
        if (formTitleField) {
          label = selectedRow[formTitleField];

          const parentRouteMatchedMeta = parentRouteMatched?.meta as IRouteMeta;
          if (parentRouteMatchedMeta?.translateCollapsibleTableTitleField && label) {
            label = t(label);
          }
        } else {
          console.warn('route', route, ' does not have formTitleField specified! ');
        }
      }

      let path = routeMatched.path.replace(':id', route.params['id'] as string);
      breadcrumbsItems.push(new BreadcrumbItem(label, path, 2));
    };

    const addItemForCollapsibleContainer = (routeMatched: RouteRecordNormalized) => {
      const containerData = coreStore.getTableBasedContainerData(AppCoreEnums.ContainerType.CollapsibleTableContainer).value;
      const id = route.params['id'];
      const selectedRow = containerData?.options?.rows.find((x) => x[containerData.options.idPropertyName] == id);
      if (selectedRow) {
        let path = routeMatched.path.replace(':id', route.params['id'] as string);
        breadcrumbsItems.push(new BreadcrumbItem(selectedRow[formTitleField], path, 2));
      }
    };

    const addItemForTabsContainerChildRoute = (routeMatched: RouteRecordNormalized) => {
      let path = routeMatched.path.replace(':id', route.params['id'] as string);
      label = tabsContainerMeta?.tabs?.find((x) => x.to === routeMatched.name)?.label || '';

      breadcrumbsItems.push(new BreadcrumbItem(t(label), path, 3));
    };

    const addItemForPageWithTabsContainerAsParent = (routeMatched: RouteRecordNormalized) => {
      let path = routeMatched.path.replace(':id', route.params['id'] as string);
      label = tabsContainerMeta?.tabs?.find((x) => x.to === routeMatched.name)?.label || '';
      if (label) label = t(label);

      breadcrumbsItems.push(new BreadcrumbItem(label, path, 3));
    };

    const addItemForSideMenuContainerChild = (routeMatched: RouteRecordNormalized) => {
      const sideMenuItems = (parentRouteMatched!.meta as IRouteMeta).sideMenuItems || [];
      const relatedSideMenuItem = sideMenuItems.find((x) => x.route == routeMatched.name);
      if (relatedSideMenuItem != null) {
        let path = routeMatched.path.replace(':id', route.params['id'] as string);
        breadcrumbsItems.push(new BreadcrumbItem(t(relatedSideMenuItem.name) || '', path, 3));
      }
    };
    await nextTick();

    let label = '';
    let formTitleField = '';
    const breadcrumbsItems = [] as BreadcrumbItem[];
    let tabsContainerMeta: IRouteMeta | undefined = undefined;
    let parentRouteMatched: RouteRecordNormalized | undefined = undefined;

    for (const routeMatched of route.matched.filter((x) => x.name != ROUTE_MAIN && x.name != ROUTE_CHAT)) {
      label = '';
      const routeMatchedMeta = routeMatched.meta as IRouteMeta;
      const menuItem = getMenuItem(routeMatched.name?.toString() || '');

      if (routeMatchedMeta.containerType === AppCoreEnums.ContainerType.CollapsibleTableContainer) {
        formTitleField = routeMatchedMeta.collapsibleTableTitleField || '';
      }

      if (routeMatchedMeta.containerType === AppCoreEnums.ContainerType.TabsContainer) tabsContainerMeta = routeMatchedMeta;

      if (menuItem) {
        //
        // Fist level Page related route
        //
        var breadcrumbItem = new BreadcrumbItem(menuItem.label?.toString() || '', routeMatched.path, 1);
        if (breadcrumbsItems.length == 0) {
          breadcrumbItem.icon = menuItem.icon;
        }
        breadcrumbsItems.push(breadcrumbItem);
      } else {
        //
        // 2+ Level Pages related routes
        //
        switch (routeMatchedMeta.containerType) {
          case AppCoreEnums.ContainerType.TabsContainer:
            await addItemForTabsContainer(routeMatched, parentRouteMatched);
            break;

          case AppCoreEnums.ContainerType.FormsContainer:
            switch ((parentRouteMatched?.meta as IRouteMeta).containerType) {
              case AppCoreEnums.ContainerType.TabsContainer:
                addItemForTabsContainerChildRoute(routeMatched);
                break;

              case AppCoreEnums.ContainerType.SideMenuContainer:
                addItemForSideMenuContainerChild(routeMatched);
                break;
            }
            break;

          case AppCoreEnums.ContainerType.SideMenuContainer:
            break;

          case AppCoreEnums.ContainerType.CollapsibleContainer:
            console.log('routeMatchedMeta CollapsibleContainer', routeMatchedMeta);
            addItemForCollapsibleContainer(routeMatched);
            break;

          case AppCoreEnums.ContainerType.TableContainer:
            addItemForPageWithTabsContainerAsParent(routeMatched);
            break;

          case AppCoreEnums.ContainerType.EmptyContainer:
          case AppCoreEnums.ContainerType.FormsContainer:
            switch ((parentRouteMatched?.meta as IRouteMeta)?.containerType) {
              case AppCoreEnums.ContainerType.SideMenuContainer:
                addItemForSideMenuContainerChild(routeMatched);
                break;

              case AppCoreEnums.ContainerType.TabsContainer:
                addItemForTabsContainerChildRoute(routeMatched);
                break;
            }

            break;
        }
      }
      parentRouteMatched = routeMatched;
    }

    return breadcrumbsItems;
  };

  return {
    buildBreadcrumbItems,
  };
};
